/**
 * WaterNowSheet — one-tap Rachio run, opened from a push notification.
 *
 * Flow:
 *   1. Push notification fires for "Zone 2 at 41% — ACTION"
 *   2. User taps push → service worker opens /pulse?run_zone=zone-2&min=6
 *   3. This sheet auto-opens, shows the zone + current moisture +
 *      a 24h sparkline that proves the trip (smart enhancement)
 *   4. User taps "Run 6 min" → POST /api/rachio/zone/start
 *   5. Sheet flips to "Watering…" with a confirmation, then closes
 *
 * Falls back to a "We don't recognize this zone" panel if the deep link
 * has a stale or unmapped zone_id.
 */
import React, { useEffect, useState } from 'react';
import { Droplets, X, Play, CheckCircle2, Loader2 } from 'lucide-react';
import { API_URL } from '../../config/api';
import { MoistureSparkline } from './MoistureSparkline';

export function WaterNowSheet({ zoneId, minutes, onClose }) {
  const [rule, setRule] = useState(null);
  const [zoneName, setZoneName] = useState(null);
  const [status, setStatus] = useState('ready'); // ready | running | success | error
  const [error, setError] = useState(null);

  // Pull the rule (so we know channel + thresholds for the sparkline) and
  // the Rachio zone display name in parallel.
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [rulesRes, zonesRes] = await Promise.all([
          fetch(`${API_URL}/api/moisture-alerts/rules`, { credentials: 'include' }),
          fetch(`${API_URL}/api/rachio/zones`, { credentials: 'include' }).catch(() => null),
        ]);
        if (cancel) return;
        if (rulesRes && rulesRes.ok) {
          const data = await rulesRes.json();
          const match = (data.rules || []).find((r) => r.zone_id === zoneId);
          setRule(match || null);
          if (match?.zone_name) setZoneName(match.zone_name);
        }
        if (zonesRes && zonesRes.ok) {
          const data = await zonesRes.json();
          // /api/rachio/zones response shape varies; defensively probe
          const list =
            data?.zones || data?.devices?.[0]?.zones || data?.results || [];
          const z = list.find(
            (it) => it.id === zoneId || it.zoneId === zoneId || String(it.zoneNumber) === String(zoneId)
          );
          if (z?.name) setZoneName(z.name);
        }
      } catch (e) {
        if (!cancel) console.warn('WaterNowSheet preload:', e.message);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [zoneId]);

  const runZone = async () => {
    setStatus('running');
    setError(null);
    try {
      const durationSec = Math.max(60, Math.min(3600, Number(minutes) * 60));
      const res = await fetch(`${API_URL}/api/rachio/zone/start`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          zone_id: zoneId,
          duration_seconds: durationSec,
        }),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status} ${txt.slice(0, 120)}`);
      }
      // Receipt — powers the /activity "Alerted X · Saved Y" pill.
      // Fire-and-forget so a logging hiccup never blocks the user.
      fetch(`${API_URL}/api/moisture-alerts/water-now-log`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          zone_id: zoneId,
          zone_name: zoneName || null,
          duration_seconds: durationSec,
          source: 'push',
        }),
      }).catch(() => {});
      setStatus('success');
      // Auto-close after a brief celebration window
      setTimeout(onClose, 2200);
    } catch (e) {
      setError(e.message);
      setStatus('error');
    }
  };

  return (
    <div
      data-testid="water-now-sheet"
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md mx-auto bg-slate-900 border-t border-slate-800 rounded-t-2xl md:rounded-2xl md:border md:m-4 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-emerald-400 text-xs uppercase tracking-wider font-semibold">
            <Droplets className="w-4 h-4" /> Water Now
          </div>
          <button
            data-testid="water-now-close"
            onClick={onClose}
            className="p-1 rounded-full text-slate-400 hover:text-slate-200 hover:bg-slate-800"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="text-2xl font-bold text-slate-100 leading-tight">
          {zoneName || zoneId}
        </div>
        <div className="text-xs text-slate-400 mt-0.5">
          Triggered by your moisture rule. Tap once to run on Rachio.
        </div>

        {/* Smart enhancement — 24h sparkline showing the trip */}
        {rule ? (
          <div className="mt-4 bg-slate-950/60 border border-slate-800 rounded-xl p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase tracking-wider text-slate-500">
                Last 24 h · channel {rule.channel}
              </span>
              <span className="text-[10px] text-slate-500">
                <span className="inline-block w-2 h-2 rounded-full bg-amber-400 mr-1" />
                heads-up <b className="text-slate-200">{rule.heads_up_pct}%</b>
                <span className="inline-block w-2 h-2 rounded-full bg-rose-400 ml-2 mr-1" />
                action <b className="text-slate-200">{rule.action_pct}%</b>
              </span>
            </div>
            <MoistureSparkline
              channel={rule.channel}
              hours={24}
              width={360}
              height={64}
              headsUp={rule.heads_up_pct}
              action={rule.action_pct}
              testid="water-now-sparkline"
            />
          </div>
        ) : (
          <div className="mt-4 bg-amber-950/40 border border-amber-900/60 rounded-xl p-3 text-xs text-amber-300 leading-snug">
            We don't have a moisture rule on file for <b>{zoneId}</b>. You
            can still run the zone — but consider adding a rule from{' '}
            <i>Alerts → Moisture Alerts</i> so we can trip on its behalf
            next time.
          </div>
        )}

        {/* Run button — the real payoff */}
        {status === 'success' ? (
          <div
            data-testid="water-now-success"
            className="mt-4 w-full px-4 py-3 rounded-xl bg-emerald-600/20 border border-emerald-700 flex items-center gap-2 text-emerald-300 text-sm font-semibold"
          >
            <CheckCircle2 className="w-5 h-5" />
            Zone is watering — Rachio queued the run.
          </div>
        ) : (
          <button
            data-testid="water-now-run-btn"
            onClick={runZone}
            disabled={status === 'running'}
            className="mt-4 w-full px-4 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-base font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {status === 'running' ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Triggering Rachio…
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                Run {minutes} min on Rachio
              </>
            )}
          </button>
        )}

        {error && (
          <div
            data-testid="water-now-error"
            className="mt-3 bg-rose-950/40 border border-rose-900 rounded-lg p-2.5 text-xs text-rose-300 leading-snug"
          >
            Couldn't start the zone: {error}
            <div className="text-slate-400 mt-1">
              If your Rachio token expired, reconnect at{' '}
              <i>Profile → Rachio</i>.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
